// Captura de UTM y referrer al primer landing del visitor (first-touch
// attribution). El payload se persiste en localStorage hasta que se manda
// con el signup; sobrevive navegación entre páginas y refresh.
//
// First-touch: si el visitor llega vía mailing y después da una vuelta por
// el sitio, no perdemos el origen original. Solo sobreescribimos si llega
// con UTM nuevos en una visita posterior (eso pisa la atribución previa
// — pattern aceptado por GA/HubSpot).

const STORAGE_KEY = 'pm_utm';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

// Solo aceptamos http/https como referrer. document.referrer puede traer
// schemes de apps nativas (android-app://com.linkedin.android/,
// ios-app://..., etc.) cuando el user abre el enlace desde LinkedIn/FB/
// WhatsApp mobile. Esos schemes rompen validaciones de URL en el backend
// (y no aportan atribucion util, ya lo captamos por User-Agent). Si el
// referrer no es http/https, lo descartamos.
const sanitizeReferrer = (raw) => {
    if (!raw || typeof raw !== 'string') return '';
    const s = raw.trim();
    if (s.startsWith('http://') || s.startsWith('https://')) return s;
    return '';
};

const readFromUrl = () => {
    const params = new URLSearchParams(window.location.search || '');
    const captured = {};
    let hasAny = false;
    UTM_KEYS.forEach((key) => {
        const value = params.get(key);
        if (value) {
            captured[key] = value.slice(0, 200);
            hasAny = true;
        }
    });
    return hasAny ? captured : null;
};

const readFromStorage = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

const writeToStorage = (payload) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
        // Cuota llena o storage deshabilitado: la atribución se pierde
        // silenciosamente. Mejor que romper el signup.
    }
};

// Limpia los utm_* del URL bar SIN recargar la página. Lo llamamos
// después de guardar a localStorage para que el user no vea utm_source=
// mailing&utm_campaign=lanzamiento en la barra cuando llega vía short
// URL /r/{code} (el browser redirige a la URL larga; queremos que apenas
// se cargue el SPA borremos los params para una experiencia limpia).
const cleanUtmFromUrlBar = () => {
    try {
        const url = new URL(window.location.href);
        let changed = false;
        UTM_KEYS.forEach((k) => {
            if (url.searchParams.has(k)) {
                url.searchParams.delete(k);
                changed = true;
            }
        });
        if (changed) {
            const cleanQs = url.searchParams.toString();
            const newUrl = `${url.pathname}${cleanQs ? '?' + cleanQs : ''}${url.hash || ''}`;
            window.history.replaceState({}, '', newUrl);
        }
    } catch {
        // window/URL no disponibles (SSR) o URL inválida — sin acción.
    }
};

// Llamar una vez al montar el App. Idempotente — si ya hay UTM guardado y
// no llegan nuevos, no hace nada. Si llegan nuevos, los pisa (last-touch
// con first-touch fallback). Después limpia la URL bar para que el user
// no vea los UTM en la barra de direcciones.
export const captureUtmFromUrl = () => {
    const fromUrl = readFromUrl();
    const stored = readFromStorage();
    if (!fromUrl && stored) return; // Ya capturado, nada nuevo.
    if (!fromUrl && !stored) {
        // Primer landing sin UTM: guardamos al menos el referrer por si
        // viene de fuera y no hay query string.
        const referrer = sanitizeReferrer(document.referrer);
        if (referrer && !referrer.includes(window.location.host)) {
            writeToStorage({ referrer: referrer.slice(0, 500) });
        }
        return;
    }
    // fromUrl tiene algo: actualizamos. Conservamos referrer existente si
    // el nuevo landing no trae uno nuevo (raro pero posible).
    const referrer = sanitizeReferrer(document.referrer) || stored?.referrer || '';
    writeToStorage({
        ...fromUrl,
        referrer: referrer && !referrer.includes(window.location.host)
            ? referrer.slice(0, 500)
            : (stored?.referrer || ''),
    });
    // Limpia los utm_* del URL bar para que el visitante no los vea —
    // especialmente importante en flujos /r/{code} donde el redirect
    // del backend deja los UTM visibles momentáneamente.
    cleanUtmFromUrlBar();
};

// Devuelve el payload UTM listo para mandar al backend en el signup.
// Siempre devuelve los 6 campos (vacíos si no hay data) para que el
// serializer no los marque como missing.
export const getStoredUtm = () => {
    const stored = readFromStorage() || {};
    return {
        utm_source: stored.utm_source || '',
        utm_medium: stored.utm_medium || '',
        utm_campaign: stored.utm_campaign || '',
        utm_term: stored.utm_term || '',
        utm_content: stored.utm_content || '',
        referrer: stored.referrer || '',
    };
};
